/**
 * Modal to pick which existing task to link (first step of link flow).
 *
 * @module linkTaskSelectModal
 */
import { useEffect, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../../../enums/modalId";
import type { LinkTaskCandidate } from "./linkTaskTypes";

/**
 * Props for {@link LinkTaskSelectModal}.
 */
interface LinkTaskSelectModalProps {
  onClose: () => void;
  candidates: LinkTaskCandidate[];
  onProceedToConfirm: (task: LinkTaskCandidate) => void;
}

/**
 * Renders the radio list to choose a task, then closes and notifies parent to show confirm.
 *
 * @param props - Close, candidates, and proceed callback.
 * @returns Oasis modal for task selection.
 */
const LinkTaskSelectModal = ({
  onClose,
  candidates,
  onProceedToConfirm,
}: LinkTaskSelectModalProps) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(
    candidates[0]?.id,
  );

  useEffect(() => {
    openModal(ModalId.LINK_EXISTING_TASK_MODAL);
  }, []);

  useEffect(() => {
    if (
      selectedId &&
      !candidates.some((candidate) => candidate.id === selectedId)
    ) {
      setSelectedId(candidates[0]?.id);
    }
  }, [candidates, selectedId]);

  const selectedTask = candidates.find(
    (candidate) => candidate.id === selectedId,
  );

  const dismiss = () => {
    closeModal(ModalId.LINK_EXISTING_TASK_MODAL, onClose);
  };

  const handleLink = () => {
    if (!selectedTask) {
      return;
    }
    onProceedToConfirm(selectedTask);
  };

  return (
    <Modal
      dialogId={ModalId.LINK_EXISTING_TASK_MODAL}
      title="Link Existing Task"
      saveButtonText="Link"
      cancelText="Cancel"
      onSave={handleLink}
      onCancel={dismiss}
      onClose={dismiss}
      disableSave={!selectedTask}
    >
      <div className="flex w-full flex-col gap-3 pb-1 pt-1">
        <p className="text-tiny font-normal leading-[1.25rem] text-primaryGray-1">
          Select the task which you want to link
        </p>
        {candidates.length === 0 ? (
          <p className="text-tiny text-primaryGray-7">
            No tasks are available to link.
          </p>
        ) : (
          <ul className="flex flex-col items-start gap-2 overflow-auto">
            {candidates.map((candidate) => {
              const inputId = `link-task-option-${candidate.id}`;
              return (
                <li key={candidate.id}>
                  <label
                    htmlFor={inputId}
                    className="flex cursor-pointer items-start gap-2"
                  >
                    <input
                      id={inputId}
                      type="radio"
                      name="link-existing-task"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#0066FF]"
                      checked={selectedId === candidate.id}
                      onChange={() => setSelectedId(candidate.id)}
                    />
                    <span className="text-tiny font-normal leading-[1.25rem] text-primaryGray-1">
                      {candidate.title}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default LinkTaskSelectModal;
